import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { FONT_MAIN, glassCard, beepAcesso } from "./MobileFamiliares";
import { ArrowLeft, CameraOff, CheckCircle2, Loader2, QrCode, XCircle } from "lucide-react";

const SCANNER_ID = "appmobile-qracesso-reader";
const COOLDOWN_MS = 5000;

const MOTIVOS = {
  ponto_invalido: "Ponto de acesso inválido ou inativo. Avise a administração.",
  fabricante_nao_suportado: "Este ponto ainda não é acionável pelo app (fase atual: somente ESP32).",
  usuario_nao_encontrado: "Cadastro não encontrado. Faça login novamente.",
  inativo: "Seu cadastro está inativo. Procure a administração.",
  bloqueado: "Acesso bloqueado. Procure a administração.",
  fora_validade: "Cadastro fora da validade. Procure a administração.",
  falta_autorizacao: "Sem autorização ativa em nenhuma unidade.",
};

function detectarOS() {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return "web";
}

export default function MobileQrAcesso({ origem, eql, cpf, facialToken, onClose }) {
  const [fase, setFase] = useState("iniciando");
  const [resultado, setResultado] = useState(null);
  const scannerRef = useRef(null);
  const processandoRef = useRef(false);
  const ultimoRef = useRef({ ponto: null, at: 0 });
  const invalidoRef = useRef(0);

  const pararScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (s) {
      scannerRef.current = null;
      try { await s.stop(); } catch { /* já parado */ }
      try { s.clear(); } catch { /* noop */ }
    }
  }, []);

  const enviar = useCallback(async (ponto, emp) => {
    processandoRef.current = true;
    setFase("enviando");
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);
    try {
      const r = await fetch(`${API}/public/acesso-qr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({
          origem, eql, cpf, ponto,
          facial_token: facialToken || undefined,
          empreendimento_codigo: emp || undefined,
          device_info: { app: "helpdesk", os: detectarOS(), ver: "1.1.3" },
          timestamp: new Date().toISOString(),
        }),
      });
      const d = await r.json();
      if (d.ok && (d.resultado === "acesso_liberado" || d.resultado === "acesso_negado")) {
        setResultado(d);
        setFase("resultado");
      } else {
        toast.error(d.motivo === "facial_requerido"
          ? "Validação facial expirada. Feche e valide o rosto novamente."
          : "Não foi possível acionar agora. Tente novamente.");
        setFase("scanning");
      }
    } catch {
      toast.error("Não foi possível acionar agora. Tente novamente.");
      setFase("scanning");
    } finally {
      clearTimeout(timer);
      processandoRef.current = false;
    }
  }, [origem, eql, cpf, facialToken]);

  const onScan = useCallback((texto) => {
    if (processandoRef.current) return;
    let payload = null;
    try { payload = JSON.parse(texto); } catch { payload = null; }
    if (!payload || payload.t !== "brave_acesso" || !payload.ponto) {
      const agora = Date.now();
      if (agora - invalidoRef.current > 3000) {
        invalidoRef.current = agora;
        toast.error("Este QR não é um QR de acesso Brave.");
      }
      return;
    }
    const agora = Date.now();
    if (payload.ponto === ultimoRef.current.ponto && agora - ultimoRef.current.at < COOLDOWN_MS) return;
    ultimoRef.current = { ponto: payload.ponto, at: agora };
    enviar(payload.ponto, payload.emp);
  }, [enviar]);

  const iniciar = useCallback(async () => {
    setFase("iniciando");
    setResultado(null);
    try {
      const s = new Html5Qrcode(SCANNER_ID, false);
      scannerRef.current = s;
      await s.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 230, height: 230 } },
        onScan,
        () => {}
      );
      setFase("scanning");
    } catch {
      scannerRef.current = null;
      setFase("erro_camera");
    }
  }, [onScan]);

  useEffect(() => {
    iniciar();
    return () => { pararScanner(); };
  }, [iniciar, pararScanner]);

  // resultado (liberado/negado): beep de confirmação + fecha sozinho após 5s voltando ao dashboard
  useEffect(() => {
    if (fase !== "resultado") return undefined;
    beepAcesso(resultado?.resultado === "acesso_liberado");
    const t = setTimeout(async () => { await pararScanner(); onClose(); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const liberado = resultado?.resultado === "acesso_liberado";

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-qracesso-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-qracesso-voltar" aria-label="Voltar"
          onClick={async () => { await pararScanner(); onClose(); }}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }}
          data-testid="appmobile-qracesso-titulo">QR Acesso</h1>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28 flex flex-col items-center">
        <div className="w-full max-w-sm rounded-[14px] overflow-hidden relative" style={glassCard}
          data-testid="appmobile-qracesso-scanner">
          <div id={SCANNER_ID} className="w-full aspect-square" style={{ background: "#0a0b10" }} />
          {fase === "iniciando" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
              data-testid="appmobile-qracesso-iniciando">
              <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#8ab4ff" }} />
              <p className="text-xs" style={{ color: "#8d90a0" }}>Abrindo câmera...</p>
            </div>
          )}
          {fase === "enviando" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
              style={{ background: "rgba(10,11,16,0.7)" }} data-testid="appmobile-qracesso-enviando">
              <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#8ab4ff" }} />
              <p className="text-xs" style={{ color: "#c3c6d7" }}>Acionando...</p>
            </div>
          )}
          {fase === "erro_camera" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
              data-testid="appmobile-qracesso-erro-camera">
              <CameraOff className="h-8 w-8" style={{ color: "#ffb4ab" }} />
              <p className="text-xs" style={{ color: "#c3c6d7" }}>
                Não conseguimos acessar a câmera. Libere a permissão de câmera para o app e tente novamente.
              </p>
              <button type="button" data-testid="appmobile-qracesso-camera-retry" onClick={iniciar}
                className="rounded-xl px-4 py-2.5 text-xs font-semibold text-white" style={{ background: "#2563EB" }}>
                Tentar novamente
              </button>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs px-6" style={{ color: "#8d90a0" }}
          data-testid="appmobile-qracesso-status">
          {fase === "scanning"
            ? "Aponte a câmera para o QR Code fixado no ponto de acesso."
            : fase === "resultado"
              ? "Leitura concluída."
              : " "}
        </p>

        {fase === "erro_camera" && (
          <div className="mt-4 flex items-center gap-2 text-[11px]" style={{ color: "#8d90a0" }}>
            <QrCode className="h-4 w-4" />
            <span>O scan também funciona no navegador (Chrome/Safari) com HTTPS.</span>
          </div>
        )}
      </main>

      {fase === "resultado" && resultado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          data-testid="appmobile-qracesso-resultado-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5 text-center" style={glassCard}>
            {liberado
              ? <CheckCircle2 className="h-12 w-12 mx-auto" style={{ color: "#7ef0a0" }} data-testid="appmobile-qracesso-icone-liberado" />
              : <XCircle className="h-12 w-12 mx-auto" style={{ color: "#ffb4ab" }} data-testid="appmobile-qracesso-icone-negado" />}
            <h2 className="mt-3 text-base font-bold"
              style={{ color: liberado ? "#7ef0a0" : "#ffb4ab" }}
              data-testid="appmobile-qracesso-resultado-titulo">
              {liberado ? "Acesso liberado" : "Acesso negado"}
            </h2>
            <div className="mt-2 text-xs space-y-1" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-qracesso-resultado-detalhe">
              {liberado ? (
                <>
                  {resultado.dispositivo && <p>{resultado.dispositivo}</p>}
                  {resultado.eql_registrada && <p>Unidade {resultado.eql_registrada}</p>}
                  {resultado.sentido && (
                    <p data-testid="appmobile-qracesso-sentido">
                      Sentido: {resultado.sentido === "S" ? "Saída" : "Entrada"}
                    </p>
                  )}
                  <p style={{ color: "#8d90a0" }}>O relé foi acionado (pulso enfileirado).</p>
                  {resultado.dispositivo_online === false && (
                    <p style={{ color: "#f4a261" }} data-testid="appmobile-qracesso-offline">
                      Dispositivo pode estar offline — se não abrir, avise a portaria.
                    </p>
                  )}
                </>
              ) : (
                <p>{MOTIVOS[resultado.motivo] || resultado.detalhe || "Acesso não autorizado neste ponto."}</p>
              )}
            </div>
            <div className="mt-4 h-1 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}
              data-testid="appmobile-qracesso-resultado-autoclose" aria-label="Fechando automaticamente em 5 segundos">
              <div className="mobile-countdown-bar h-full rounded-full"
                style={{ background: liberado ? "#7ef0a0" : "#ffb4ab" }} />
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" data-testid="appmobile-qracesso-novamente"
                onClick={() => { setResultado(null); setFase(scannerRef.current ? "scanning" : "iniciando"); if (!scannerRef.current) iniciar(); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: "#2563EB" }}>
                Escanear novamente
              </button>
              <button type="button" data-testid="appmobile-qracesso-fechar"
                onClick={async () => { await pararScanner(); onClose(); }}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
